import { orderBy } from 'lodash';

/**
 * convertToAPIRequestBody method, this function converts the json format within the components state into the required format accepted
 * by the API
 * @param isWidgetDefault -> bool value to set if particular widget is default or not
 * @param data -> the data from the state of the component calling this function to format to cater to the API request body
 * below is the format:
 * [
 *    groupName,
 *    isDefault (not required),
 *    isVisible,
 *    items: [
 *      index,
 *      isVisible,
 *      itemName,
 *      labelName,
 *    ],
 * ]
 */

export const convertToAPIRequestBody = (data, isWidgetDefault, params) => {
  if (Array.isArray(data)) {
    // determine format for widget level details here
    const formattedData = {
      widgetGroupId: params.widgetGroupId,
      widgetId: params.widgetId,
      widgetName: params.widgetName,
      widgetIndex: params.widgetIndex,
      isVisible: params.isVisible,
      isDefault: isWidgetDefault
    };

    // map each column we have within the data passed
    const formattedGroups = data.map((currentGroup, currentGroupIndex) => {
      const { items } = currentGroup;
      // determine format for column level here for every single column existing within the "data" param
      // provided

      const formattedGroup = {
        columnId: currentGroup.columnId
          ? currentGroup.columnId
          : `${formattedData.widgetId}_${currentGroup.groupName}_${currentGroupIndex}`,
        columnName: currentGroup.groupName,
        columnIndex: currentGroupIndex,
        columnVisibility: currentGroup.isVisible,
        isDefault: currentGroup.isDefault ? currentGroup.isDefault : false
      };

      // iterate and push items within the respective column with the required format
      if (Array.isArray(items) && items.length > 0) {
        const formattedItems = [];
        items.forEach((currentItem, currentItemIndex) => {
          if (!currentItem.isAdding) {
            formattedItems.push({
              itemName: currentItem.itemName,
              itemLabel: currentItem.labelName,
              itemIndex: currentItemIndex,
              visibility: currentItem.isVisible,
              searchEnabled: currentItem.searchEnabled,
              sortEnabled: currentItem.sortEnabled,
              filterEnabled: currentItem.filterEnabled
            });
          }
        });

        formattedGroup.columnDetail = formattedItems;
      }

      return formattedGroup;
    });

    formattedData.widgetConfig = formattedGroups;

    return formattedData;
  }

  return null;
};

/**
 * convertAPIResponseToState method, this function converts the API response to the respective format we are working on
 * within our components
 * @param data -> the data from the API response to be converted to our format
 * below is the format currently being returned by the API:
 * {
 *    widgetId,
 *    widgetName,
 *    isDefault,
 *    widgetConfig: [
 *      columnId,
 *      columnName,
 *      columnIndex,
 *      columnVisibility,
 *      isDefault,
 *      columnDetail: [
 *        itemName,
 *        itemLabel,
 *        itemIndex,
 *        visibility,
 *      ]
 *    ],
 * }
 */

export const convertAPIResponseToState = (data) => {
  if (data) {
    return orderBy(data.uiWidgetGroups, ['widgetGroupIndex'], ['asc']).map(
      (wg) => {
        return {
          groupName: wg.widgetGroupId,
          isVisible: true,
          widgets: orderBy(wg.widgets, ['widgetIndex'], ['asc']).map(
            (widget) => {
              const { widgetConfig } = widget;
              const formattedGroups = [];
              if (Array.isArray(widgetConfig) && widgetConfig.length > 0) {
                // iterate every single column we have from the response and format in accordingly
                const sortedWidgetConfig = orderBy(
                  widgetConfig,
                  ['columnIndex'],
                  ['asc']
                );
                sortedWidgetConfig.forEach((currentGroup) => {
                  const formattedGroup = {
                    groupName: currentGroup.columnName,
                    isDefault: currentGroup.isDefault,
                    isVisible: currentGroup.columnVisibility,
                    columnId: currentGroup.columnId
                  };

                  const { columnDetail } = currentGroup;
                  const formattedItems = [];
                  if (Array.isArray(columnDetail) && columnDetail.length > 0) {
                    const sortedColumnDetail = orderBy(
                      columnDetail,
                      ['itemIndex'],
                      ['asc']
                    );

                    // iterate every single item under the particular column into the format desired
                    sortedColumnDetail.forEach(
                      (currentItem, currentItemIndex) => {
                        formattedItems.push({
                          itemName: currentItem.itemName,
                          labelName: currentItem.itemLabel,
                          isVisible: currentItem.visibility,
                          index: currentItem.itemIndex,
                          searchEnabled: currentItem.searchEnabled,
                          sortEnabled: currentItem.sortEnabled,
                          filterEnabled: currentItem.filterEnabled
                        });
                      }
                    );
                  }
                  formattedGroup.items = formattedItems;
                  formattedGroups.push(formattedGroup);
                });
              }
              return { groupName: widget.widgetId, groups: formattedGroups };
            }
          )
        };
      }
    );
  }

  return null;
};

export const updateGroup = (group, groupIndex, groups) => {
  //compute item to label dict
  const dict = group.items.reduce((org, item) => {
    org[item.itemName] = item.labelName;
    return org;
  }, {});

  // update group
  return groups.map((currentGroup, currentGroupIndex) => {
    if (currentGroupIndex === groupIndex) {
      return group;
    } else {
      //check for items with new label
      const tempGroup = {
        ...currentGroup,
        items: currentGroup.items.map((item) => {
          if (dict[item.itemName]) item.labelName = dict[item.itemName];
          return item;
        })
      };
      return tempGroup;
    }
  });
};

export const toggleGroupVisibility = (groupIndex, groups) => {
  const currentGroup = groups[groupIndex];
  let updatedGroup = { ...currentGroup };

  updatedGroup.isVisible = !currentGroup.isVisible;
  return groups.map((groupItem, currentGroupIndex) => {
    if (currentGroupIndex === groupIndex) {
      const { items } = groupItem;
      const updatedItems = items.map((currentItem) => {
        return {
          ...currentItem,
          isVisible: updatedGroup.isVisible
        };
      });

      return {
        ...groupItem,
        isVisible: updatedGroup.isVisible,
        items: updatedItems
      };
    } else {
      return groupItem;
    }
  });
};

/**
 * parseColumnNames method, takes widgetItemOperations and selectedAlertWidget to return
 * possible items for the given widget
 * within our components
 * @param selectedAlertWidget -> currently selected widget
 * @param widgetItemOperations -> item operations for the given page
 */
export const parseColumnNames = (selectedAlertWidget, widgetItemOperations) => {
  const widget = widgetItemOperations?.data?.widgetItems?.find(
    (w) => w.widgetId === selectedAlertWidget
  );
  return widget
    ? widget.itemsOperations.map((io) => {
        return {
          ...io,
          label: io.itemName,
          value: io.itemName
        };
      })
    : [];
};
